import {
  getProfesionDetail,
  getProfesionesSimilares,
} from "@/services/relation-fields.service";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import {
  Profession,
  relationFieldSchema,
} from "../schema/relation-fields.schema";
import { useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "@/utils/query-string";
import { AppRoutes } from "@/constants/routes";
import { handleUpdateProfessionHandler } from "../schema/handler-functions";
import { zodResolver } from "@hookform/resolvers/zod";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { AppColors } from "@/styles/colors";
import { InputField } from "@/components/input-field/InputField";
import { SimilarRelationFieldsAlert } from "@/components/common/section-components/alert/SimilarRelationFieldsAlert";
import { useRoles } from "@/hooks/useRoles";

export const EditProfessionPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { id } = useParams();

  const { control, setValue } = useForm({
    resolver: zodResolver(relationFieldSchema),
  });

  const [profession, setProfession] = useState<Profession | undefined>();
  const [loadingHobbie, setLoadingHobbie] = useState<boolean>(false);

  const getHobbie = async (id: number) => {
    setLoadingHobbie(true);
    const response = await getProfesionDetail({ id });

    setProfession(response);
    setLoadingHobbie(false);
  };

  useEffect(() => {
    if (isActive && id) {
      getHobbie(Number(id));
    }
  }, [isActive, id]);

  useEffect(() => {
    if (profession) {
      setValue("nombre", profession?.nombre);
    }
  }, [profession]);

  const watchedValues = useWatch({
    control,
  });

  const hasNoFilledValues = useMemo(() => {
    const { nombre } = watchedValues || {};

    return isEmpty(nombre);
  }, [watchedValues]);

  const [loadingCreateProfession, setLoadingCreateProfession] =
    useState<boolean>(false);
  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(AppRoutes.RELATION_FIELDS);
  };

  const { fetchPermissions } = useRoles();
  const [similarProfessions, setSimilarProfessions] = useState<
    Profession[] | undefined
  >([]);

  const handleCreateHobbie = () => {
    handleUpdateProfessionHandler({
      setLoadingCreateProfession,
      navigate,
      watchedValues,
      id: Number(id),
      fetchPermissions
    });
  };

  const [loadingSimilarProfessions, setLoadingSimilarProfessions] =
    useState<boolean>(false);

  const getSimilarProfessions = async (nombre: string) => {
    setLoadingSimilarProfessions(true);
    const response = await getProfesionesSimilares({
      nombre,
      excluidos: [Number(profession?.id)],
    });

    setSimilarProfessions(response);

    setLoadingSimilarProfessions(false);
  };

  useEffect(() => {
    const { nombre } = watchedValues || {};
    if (!nombre) return;

    const timeout = setTimeout(() => {
      getSimilarProfessions(nombre);
    }, 500);

    return () => clearTimeout(timeout);
  }, [watchedValues?.nombre]);
  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Editar profesión"
        addButtonLabel="Confirmar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateHobbie}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreateProfession}
        loading={loadingCreateProfession || loadingHobbie}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                inputName="nombre"
                labelText="Nombre"
                control={control}
                disabled={loadingCreateProfession || loadingHobbie}
                placeholder="Ingresar nombre de la profesión"
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>

      {/* Hobbies similares */}
      {Boolean(similarProfessions?.length) &&
        !isEmpty(watchedValues?.nombre) && (
          <SimilarRelationFieldsAlert
            title="Hay profesiones similares existentes"
            legend="Evite repeticiones"
            similarItems={similarProfessions?.map((el) => el.nombre) || []}
            loadingSimilarElements={loadingSimilarProfessions}
          />
        )}
    </Flex>
  );
};
